import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Wallet, Clock, RefreshCw, Wifi, WifiOff, Smartphone, Hash,
  CheckCircle2, CreditCard, AlertCircle,
  ListOrdered, ShieldCheck, Hourglass, ChevronLeft, ChevronRight,
} from 'lucide-react';
import orderService from '../../api/services/orderService';
import paymentService from '../../api/services/paymentService';
import socketService from '../../api/socket';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatAmount = (amount, currency = 'XAF') => {
  const n = Number(amount) || 0;
  const label = currency === 'XAF' ? 'FCFA' : currency;
  return `${n.toLocaleString('fr-FR')} ${label}`;
};

const formatTime = (date) =>
  date ? new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—';

const isToday = (date) => date && new Date(date).toDateString() === new Date().toDateString();

const cleanPhone = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s || s.toUpperCase() === 'N/A' || s.toLowerCase().endsWith('@whatsapp.local')) return '';
  return s;
};

// ─── Configuration des badges ─────────────────────────────────────────────────

// Mapping : paymentStatus 'pending' = En attente, 'paid' = PAYÉE
const PAYMENT_STATUS = {
  paid:     { label: 'PAYÉE',      cls: 'bg-accent-green/15 text-accent-green border border-accent-green/30', Icon: CheckCircle2 },
  pending:  { label: 'En attente', cls: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30',      Icon: Clock },
  failed:   { label: 'Échec',      cls: 'bg-red-500/15 text-red-400 border border-red-500/30',                Icon: AlertCircle },
  refunded: { label: 'Remboursée', cls: 'bg-gray-500/15 text-gray-300 border border-gray-500/30',            Icon: CreditCard },
};

// paymentMethod / operator : MTN | ORANGE
const OPERATOR = {
  MTN:    { label: 'MTN MoMo',     cls: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30' },
  ORANGE: { label: 'Orange Money', cls: 'bg-orange-500/15 text-orange-400 border border-orange-500/30' },
};

// Statut d'une transaction caisse : 'Disponible' (non rattachée) | 'Utilisée' (consommée)
const CASH_STATUS = {
  Disponible: { label: 'Disponible', cls: 'bg-gray-500/15 text-gray-300 border border-gray-500/30',  Icon: Hourglass },
  Utilisée:   { label: 'Utilisée',   cls: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',   Icon: ShieldCheck },
};

const CASH_PAGE_SIZE = 50;

// ─── Sous-composants UI ───────────────────────────────────────────────────────

function KpiCard({ icon: Icon, iconBg, iconColor, label, value, sub, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="card p-5 hover:border-accent-green/30 transition-all"
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${iconBg}`}>
          <Icon className={iconColor} size={26} />
        </div>
        <div className="min-w-0">
          <p className="text-label text-sm">{label}</p>
          <p className="text-2xl font-bold text-ink truncate">{value}</p>
          {sub && <p className="text-label text-xs mt-0.5">{sub}</p>}
        </div>
      </div>
    </motion.div>
  );
}

function Badge({ cls, children, icon: Icon }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${cls}`}>
      {Icon && <Icon size={12} />}
      {children}
    </span>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function GestionVentes() {
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState('orders');
  const [wsConnected, setWsConnected] = useState(socketService.isConnected());
  const [flashId, setFlashId] = useState(null);

  // ── Flux de la caisse (transactions Mobile Money) ──────────────────────────
  const [cashSkip, setCashSkip] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('');

  // ── Commandes via React Query (cache partagé avec la page Commandes) ───────
  const { data: ordersData, isLoading: loading, isError: ordersError, isFetching: ordersFetching } = useQuery({
    queryKey: ['orders'],
    queryFn: () => orderService.getOrders({ limit: 1000 }),
  });

  const orders = (ordersData?.success && Array.isArray(ordersData.data)) ? ordersData.data : [];

  // ── Cash transactions via React Query ─────────────────────────────────────
  const { data: cashData, isLoading: cashLoading, isError: cashIsError, error: cashError, isFetching: cashFetching } = useQuery({
    queryKey: ['cash-transactions', statusFilter, operatorFilter, cashSkip],
    queryFn: () => {
      const params = { limit: CASH_PAGE_SIZE, skip: cashSkip };
      if (statusFilter) params.status = statusFilter;
      if (operatorFilter) params.operator = operatorFilter;
      return paymentService.getCashTransactions(params);
    },
    enabled: activeTab === 'cash',
  });

  const cashTx = (cashData?.success && Array.isArray(cashData.data)) ? cashData.data : [];
  const cashTotal = cashData?.meta?.total ?? cashTx.length;

  // Reset pagination when filter changes
  useEffect(() => { setCashSkip(0); }, [statusFilter, operatorFilter]);

  // ── Connexion temps réel + écoute des événements ───────────────────────────
  // Refs pour ne pas recréer les listeners socket à chaque rendu.
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return undefined;

    if (!socketService.isConnected()) {
      socketService.connect(token);
    }
    setWsConnected(socketService.isConnected());

    const handleConnect = () => setWsConnected(true);
    const handleDisconnect = () => setWsConnected(false);

    // 🟢 Paiement validé automatiquement → commande PAYÉE + transaction Utilisée
    const handleOrderUpdated = (payload) => {
      const updated = payload?.order || payload?.data || payload;
      if (!updated?._id) return;

      qc.setQueryData(['orders'], (prev) => {
        if (!prev?.data) return prev;
        const exists = prev.data.some((o) => o._id === updated._id);
        return {
          ...prev,
          data: exists
            ? prev.data.map((o) => (o._id === updated._id ? { ...o, ...updated } : o))
            : [updated, ...prev.data],
        };
      });

      setFlashId(updated._id);
      setTimeout(() => setFlashId((id) => (id === updated._id ? null : id)), 2500);

      // Refetch ciblé pour récupérer paymentMethod / paymentId complets
      orderService
        .getOrderById(updated._id)
        .then((res) => {
          const full = res?.data || res;
          if (full?._id) {
            qc.setQueryData(['orders'], (prev) => {
              if (!prev?.data) return prev;
              return { ...prev, data: prev.data.map((o) => (o._id === full._id ? { ...o, ...full } : o)) };
            });
          }
        })
        .catch(() => {});

      // Une validation fait passer une transaction caisse à "Utilisée" → invalide le cache
      if (activeTabRef.current === 'cash') {
        qc.invalidateQueries({ queryKey: ['cash-transactions'] });
      }
    };

    socketService.on('connect', handleConnect);
    socketService.on('disconnect', handleDisconnect);
    socketService.on('order:updated', handleOrderUpdated);

    return () => {
      socketService.off('connect', handleConnect);
      socketService.off('disconnect', handleDisconnect);
      socketService.off('order:updated', handleOrderUpdated);
    };
  }, []);

  // Les nouvelles commandes socket arrivent dans le cache via OrdersRealtimeContext
  const mergedOrders = useMemo(
    () => [...orders].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [orders]
  );

  // ── KPIs (données réelles) ─────────────────────────────────────────────────
  const currency = mergedOrders[0]?.currency || 'XAF';

  const caDuJour = useMemo(
    () =>
      mergedOrders
        .filter((o) => o.paymentStatus === 'paid' && isToday(o.updatedAt || o.createdAt))
        .reduce((s, o) => s + (Number(o.total) || 0), 0),
    [mergedOrders]
  );

  const enAttente = useMemo(
    () => mergedOrders.filter((o) => o.paymentStatus === 'pending').length,
    [mergedOrders]
  );

  const encaissementsDuJour = useMemo(
    () =>
      mergedOrders.filter((o) => o.paymentStatus === 'paid' && isToday(o.updatedAt || o.createdAt))
        .length,
    [mergedOrders]
  );

  // ── Rafraîchissement manuel ────────────────────────────────────────────────
  const handleRefresh = () => {
    qc.invalidateQueries({ queryKey: ['orders'] });
    if (activeTab === 'cash') qc.invalidateQueries({ queryKey: ['cash-transactions'] });
  };

  const cashPage = Math.floor(cashSkip / CASH_PAGE_SIZE) + 1;
  const cashPages = Math.max(1, Math.ceil(cashTotal / CASH_PAGE_SIZE));

  return (
    <div className="min-h-screen">
      {/* ── En-tête ───────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-4 sm:p-6 mb-6"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-ink font-semibold mb-1 flex items-center gap-2.5" style={{ fontSize: '18px' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                <Wallet size={18} className="text-accent-green" />
              </div>
              Suivi des Commandes &amp; Encaissements
            </h1>
            <p className="text-label text-sm">
              Validation des paiements 100&nbsp;% automatique · mise à jour en temps réel
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Indicateur de connexion temps réel (WebSocket) */}
            <motion.div
              animate={wsConnected ? { boxShadow: ['0 0 0 0 rgba(0,255,144,0)', '0 0 0 4px rgba(0,255,144,0.12)', '0 0 0 0 rgba(0,255,144,0)'] } : {}}
              transition={{ duration: 2, repeat: Infinity }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border ${
                wsConnected
                  ? 'bg-accent-green/10 text-accent-green border-accent-green/30'
                  : 'bg-red-500/10 text-red-400 border-red-500/30'
              }`}
            >
              {wsConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
              <span className="relative flex h-2 w-2">
                {wsConnected && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-green opacity-75" />
                )}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${wsConnected ? 'bg-accent-green' : 'bg-red-400'}`} />
              </span>
              {wsConnected ? 'Temps réel connecté' : 'Reconnexion…'}
            </motion.div>

            {/* Bouton rafraîchir */}
            <motion.button
              onClick={handleRefresh}
              disabled={ordersFetching}
              whileHover={{ scale: ordersFetching ? 1 : 1.03 }}
              whileTap={{ scale: ordersFetching ? 1 : 0.97 }}
              className="flex items-center gap-2 px-4 py-2 text-ink rounded-lg text-sm font-medium transition-all disabled:opacity-60 hover:bg-[var(--c-hover)]"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            >
              <RefreshCw size={16} className={`text-accent-green ${ordersFetching ? 'animate-spin' : ''}`} />
              {ordersFetching ? 'Synchronisation…' : 'Rafraîchir'}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* ── KPIs ──────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard
          icon={Wallet}
          iconBg="bg-accent-green/15"
          iconColor="text-accent-green"
          label="Chiffre d'affaires du jour"
          value={formatAmount(caDuJour, currency)}
          sub="Paiements validés aujourd'hui"
          delay={0.1}
        />
        <KpiCard
          icon={Clock}
          iconBg="bg-yellow-500/15"
          iconColor="text-yellow-400"
          label="En attente de paiement"
          value={enAttente}
          sub={`${enAttente > 1 ? 'commandes' : 'commande'} à encaisser`}
          delay={0.2}
        />
        <KpiCard
          icon={CheckCircle2}
          iconBg="bg-blue-500/15"
          iconColor="text-blue-400"
          label="Encaissements du jour"
          value={encaissementsDuJour}
          sub="Transactions validées aujourd'hui"
          delay={0.3}
        />
      </div>

      {/* ── Onglets ───────────────────────────────────────────────────────── */}
      <div className="flex gap-2 mb-5 p-1.5 rounded-xl w-full sm:w-fit" style={{ background: 'var(--c-primary-card)', border: '0.5px solid var(--c-border)' }}>
        {[
          { id: 'orders', label: 'Commandes clients', icon: ListOrdered, count: mergedOrders.length },
          { id: 'cash', label: 'Flux de la Caisse', icon: Smartphone, count: cashTotal },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 sm:flex-none justify-center ${
              activeTab === tab.id
                ? 'bg-accent-green text-black shadow-md'
                : 'text-text-gray hover:bg-[var(--c-hover)]'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            <span
              className={`px-1.5 py-0.5 rounded-md text-xs ${activeTab === tab.id ? 'bg-black/15 text-black' : 'text-text-gray'}`}
              style={activeTab !== tab.id ? { background: 'var(--c-hover)' } : {}}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ── Contenu des onglets ───────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {activeTab === 'orders' ? (
          <motion.div
            key="orders"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4" />
                  <p className="text-label">Chargement des commandes…</p>
                </div>
              </div>
            ) : ordersError ? (
              <ErrorBlock message="Erreur lors du chargement des commandes" onRetry={() => qc.invalidateQueries({ queryKey: ['orders'] })} />
            ) : mergedOrders.length === 0 ? (
              <EmptyBlock icon={Wallet} title="Aucune commande" text="Les encaissements apparaîtront ici en temps réel." />
            ) : (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[820px]">
                    <thead style={{ background: 'var(--c-primary-dark)', borderBottom: '0.5px solid var(--c-border)' }}>
                      <tr>
                        {['N° Commande', 'Client (WhatsApp)', 'Date', 'Montant', 'Paiement', 'Opérateur', 'ID Transaction'].map((h) => (
                          <th key={h} className="text-left p-4 text-label font-medium text-sm">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mergedOrders.map((order, i) => {
                        const pay = PAYMENT_STATUS[order.paymentStatus] || PAYMENT_STATUS.pending;
                        const op = order.paymentMethod ? OPERATOR[String(order.paymentMethod).toUpperCase()] : null;
                        const phone = cleanPhone(order.clientPhone);
                        const isFlash = flashId === order._id;
                        return (
                          <motion.tr
                            key={order._id}
                            initial={isFlash ? { backgroundColor: 'rgba(0,255,144,0.18)' } : false}
                            animate={{ backgroundColor: 'rgba(0,0,0,0)' }}
                            transition={{ duration: 2.2 }}
                            className="transition-colors hover:bg-[var(--c-hover)]"
                            style={{ borderTop: '0.5px solid var(--c-border)' }}
                          >
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {isFlash && <span className="w-1.5 h-1.5 rounded-full bg-accent-green animate-pulse" />}
                                <span className="text-accent-green font-semibold">{order.orderNumber}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <p className="text-ink">{order.clientName || 'Client'}</p>
                              {phone && <p className="text-label text-xs">{phone}</p>}
                            </td>
                            <td className="p-4 text-label text-sm">
                              {formatDate(order.createdAt)} · {formatTime(order.createdAt)}
                            </td>
                            <td className="p-4 text-ink font-bold">
                              {formatAmount(order.total, order.currency)}
                            </td>
                            <td className="p-4">
                              <Badge cls={pay.cls} icon={pay.Icon}>{pay.label}</Badge>
                            </td>
                            <td className="p-4">
                              {op ? (
                                <Badge cls={op.cls} icon={Smartphone}>{op.label}</Badge>
                              ) : (
                                <span className="text-label/50 text-xs italic">—</span>
                              )}
                            </td>
                            <td className="p-4">
                              {order.paymentId ? (
                                <span className="inline-flex items-center gap-1.5 text-label text-xs font-mono px-2 py-1 rounded-md" style={{ background: 'var(--c-primary-dark)' }}>
                                  <Hash size={12} className="text-accent-green" />
                                  {order.paymentId}
                                </span>
                              ) : (
                                <span className="text-label/50 text-xs italic">—</span>
                              )}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="cash"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {/* Filtres */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-ink text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green cursor-pointer"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              >
                <option value="">Tous les statuts</option>
                <option value="Disponible">Disponible</option>
                <option value="Utilisée">Utilisée</option>
              </select>
              <select
                value={operatorFilter}
                onChange={(e) => setOperatorFilter(e.target.value)}
                className="text-ink text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green cursor-pointer"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              >
                <option value="">Tous les opérateurs</option>
                <option value="MTN">MTN</option>
                <option value="ORANGE">Orange</option>
              </select>
              <span className="text-label text-xs ml-auto">
                {cashTotal} transaction{cashTotal > 1 ? 's' : ''} · journal anti-fraude
              </span>
            </div>

            {cashLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4" />
                  <p className="text-label">Chargement du flux caisse…</p>
                </div>
              </div>
            ) : cashIsError ? (
              <ErrorBlock message={cashError?.message || 'Erreur transactions'} onRetry={() => qc.invalidateQueries({ queryKey: ['cash-transactions'] })} />
            ) : cashTx.length === 0 ? (
              <EmptyBlock icon={Smartphone} title="Aucune transaction" text="Les paiements Mobile Money interceptés apparaîtront ici." />
            ) : (
              <>
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px]">
                      <thead style={{ background: 'var(--c-primary-dark)', borderBottom: '0.5px solid var(--c-border)' }}>
                        <tr>
                          {['Réception', 'Opérateur', 'ID Transaction', 'Montant', 'Statut', 'Client / Commande'].map((h) => (
                            <th key={h} className="text-left p-4 text-label font-medium text-sm">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cashTx.map((tx, i) => {
                          const op = OPERATOR[String(tx.operator).toUpperCase()] || OPERATOR.MTN;
                          const st = CASH_STATUS[tx.status] || CASH_STATUS.Disponible;
                          const phone = cleanPhone(tx.whatsappClient);
                          return (
                            <tr
                              key={tx._id}
                              className="transition-colors hover:bg-[var(--c-hover)]"
                              style={{ borderTop: '0.5px solid var(--c-border)' }}
                            >
                              <td className="p-4 text-sm">
                                <span className="text-ink">{formatTime(tx.receivedAt)}</span>
                                <span className="text-label text-xs"> · {formatDate(tx.receivedAt)}</span>
                              </td>
                              <td className="p-4">
                                <Badge cls={op.cls} icon={Smartphone}>{op.label}</Badge>
                              </td>
                              <td className="p-4">
                                <span className="text-label text-xs font-mono px-2 py-1 rounded-md" style={{ background: 'var(--c-primary-dark)' }}>
                                  {tx.transactionId}
                                </span>
                              </td>
                              <td className="p-4 text-ink font-bold">{formatAmount(tx.amount, 'XAF')}</td>
                              <td className="p-4">
                                <Badge cls={st.cls} icon={st.Icon}>{st.label}</Badge>
                              </td>
                              <td className="p-4">
                                {phone ? (
                                  <p className="text-ink text-sm">{phone}</p>
                                ) : (
                                  <span className="text-label/50 text-xs italic">—</span>
                                )}
                                {tx.usedAt && (
                                  <p className="text-label text-xs">Utilisée à {formatTime(tx.usedAt)}</p>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {cashPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-label text-xs">
                      Page {cashPage} / {cashPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCashSkip((s) => Math.max(0, s - CASH_PAGE_SIZE))}
                        disabled={cashSkip === 0}
                        className="flex items-center gap-1 px-3 py-2 text-ink text-sm rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--c-hover)]"
                        style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      >
                        <ChevronLeft size={16} />
                        Précédent
                      </button>
                      <button
                        onClick={() => setCashSkip((s) => s + CASH_PAGE_SIZE)}
                        disabled={cashSkip + CASH_PAGE_SIZE >= cashTotal}
                        className="flex items-center gap-1 px-3 py-2 text-ink text-sm rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--c-hover)]"
                        style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      >
                        Suivant
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Blocs d'état réutilisables ───────────────────────────────────────────────

function ErrorBlock({ message, onRetry }) {
  return (
    <div className="bg-red-500/10 border border-red-500 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <AlertCircle className="w-6 h-6 text-red-500" />
        <h3 className="text-red-500 font-semibold">Erreur</h3>
      </div>
      <p className="text-label mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
      >
        <RefreshCw size={16} />
        Réessayer
      </button>
    </div>
  );
}

function EmptyBlock({ icon: Icon, title, text }) {
  return (
    <div className="card p-8 sm:p-12 text-center">
      <Icon className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--c-text-muted)' }} />
      <h3 className="text-xl font-semibold text-ink mb-2">{title}</h3>
      <p className="text-label">{text}</p>
    </div>
  );
}
